import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  contextApi,
  ContextMember,
  ContextType,
  CreateContextData,
  FinancialContext,
} from '../../services/contextApi';
import {
  parseWeights,
  PICKABLE_SPLIT_RULES,
  SplitRule,
  splitRuleOf,
  splitWeightsOf,
  suggestedSplitRule,
  weightPercentages,
  withSplitRule,
} from '../../services/splitRules';

const CONTEXT_TYPES: ContextType[] = [
  'family',
  'shared_living',
  'trip',
  'project',
  'friends',
  'business',
  'shared',
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'BRL', 'JPY', 'CNY'];

const PALETTE = ['#1976d2', '#7b1fa2', '#00897b', '#ef6c00', '#c2185b', '#455a64'];

interface Props {
  open: boolean;
  context?: FinancialContext | null;
  onClose: () => void;
  onSubmit: (data: CreateContextData) => Promise<unknown>;
}

export const ContextFormDialog: React.FC<Props> = ({ open, context, onClose, onSubmit }) => {
  const { t } = useTranslation('contexts');
  const isEdit = Boolean(context);

  const [values, setValues] = useState<CreateContextData>({
    name: '',
    description: '',
    type: 'family',
    defaultCurrency: 'BRL',
    color: PALETTE[0],
  });
  const [includePersonal, setIncludePersonal] = useState(true);
  const [splitRule, setSplitRule] = useState<SplitRule>('none');
  // Once someone picks a rule by hand, changing the type must not override it.
  const [splitTouched, setSplitTouched] = useState(false);
  // Fixed proportions: one weight per member, typed as text so "1,5" works.
  const [members, setMembers] = useState<ContextMember[]>([]);
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setValues({
      name: context?.name ?? '',
      description: context?.description ?? '',
      type: context?.type ?? 'family',
      defaultCurrency: context?.defaultCurrency ?? 'BRL',
      color: context?.color ?? PALETTE[0],
    });
    setIncludePersonal(context?.settings?.includeInPersonalFinances !== false);
    setSplitRule(context ? splitRuleOf(context.settings) : suggestedSplitRule('family'));
    setSplitTouched(false);
    setWeights(
      Object.fromEntries(
        Object.entries(splitWeightsOf(context?.settings)).map(([userId, value]) => [userId, String(value)]),
      ),
    );
  }, [open, context]);

  const change = (field: keyof CreateContextData) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setValues((current) => ({ ...current, [field]: event.target.value }));

  const changeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as ContextType;
    setValues((current) => ({ ...current, type }));
    if (!isEdit && !splitTouched) {
      setSplitRule(suggestedSplitRule(type));
    }
  };

  const changeSplitRule = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSplitRule(event.target.value as SplitRule);
    setSplitTouched(true);
  };

  // Members to weigh: only an existing context has them (invited ones too).
  useEffect(() => {
    if (!open || !context) {
      setMembers([]);
      return undefined;
    }
    let cancelled = false;
    contextApi
      .getMembers(context.id)
      .catch(() => [] as ContextMember[])
      .then((list) => {
        if (!cancelled) {
          setMembers(list.filter((member) => member.status === 'active' || member.status === 'invited'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, context]);

  // Fixed proportions need people to weigh: a new context has only its owner.
  const splitOptions: SplitRule[] = isEdit
    ? PICKABLE_SPLIT_RULES
    : PICKABLE_SPLIT_RULES.filter((rule) => rule !== 'weighted');
  const parsedWeights = parseWeights(
    Object.fromEntries(Object.entries(weights).filter(([userId]) => members.some((member) => member.userId === userId))),
  );
  const percentages = weightPercentages(parsedWeights);

  const submit = async () => {
    if (!values.name.trim()) {
      setError(t('form.nameRequired'));
      return;
    }

    if (splitRule === 'weighted' && Object.keys(parsedWeights).length === 0) {
      setError(t('form.weightsInvalid'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Merged over the existing settings so the budget (and future keys)
      // survive the toggle; "on" removes the key — absence means the default.
      const settings = withSplitRule(
        context?.settings,
        splitRule,
        splitRule === 'weighted' ? parsedWeights : undefined,
      );
      if (includePersonal) {
        delete settings.includeInPersonalFinances;
      } else {
        settings.includeInPersonalFinances = false;
      }

      await onSubmit({ ...values, name: values.name.trim(), settings });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? t('form.editTitle') : t('form.createTitle')}</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label={t('form.name')}
              value={values.name}
              onChange={change('name')}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 100 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('form.description')}
              value={values.description}
              onChange={change('description')}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField select label={t('form.type')} value={values.type} onChange={changeType} fullWidth>
              {CONTEXT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`types.${type}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label={t('form.currency')}
              value={values.defaultCurrency}
              onChange={change('defaultCurrency')}
              fullWidth
            >
              {CURRENCIES.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label={t('form.splitRule')}
              value={splitRule}
              onChange={changeSplitRule}
              fullWidth
              helperText={t('form.splitRuleHint')}
            >
              {splitOptions.map((rule) => (
                <MenuItem key={rule} value={rule}>
                  {t(`splitRules.${rule}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {splitRule === 'weighted' && members.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('form.weights')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {t('form.weightsHint')}
              </Typography>
              <Grid container spacing={1.5}>
                {members.map((member) => (
                  <Grid item xs={12} sm={6} key={member.userId}>
                    <TextField
                      label={`${member.user?.firstName ?? member.userId}${
                        member.status === 'invited' ? ` (${t('form.pendingMember')})` : ''
                      }`}
                      value={weights[member.userId] ?? ''}
                      onChange={(event) =>
                        setWeights((current) => ({ ...current, [member.userId]: event.target.value }))
                      }
                      inputProps={{ inputMode: 'decimal' }}
                      size="small"
                      fullWidth
                      helperText={
                        percentages[member.userId] !== undefined
                          ? t('form.weightShare', { percent: percentages[member.userId] })
                          : t('form.weightOut')
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={includePersonal}
                  onChange={(event) => setIncludePersonal(event.target.checked)}
                />
              }
              label={t('form.includePersonal')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
              {t('form.includePersonalHint')}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t('actions.cancel')}
        </Button>
        <Button onClick={submit} variant="contained" disabled={isSaving}>
          {isEdit ? t('actions.save') : t('actions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
